import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, ApiError } from '../lib/apiClient';
import { UnifiedStatusBadge } from './unified';
import SourceBadge from './SourceBadge';
import InventoryDashboard from './InventoryDashboard';
import StorageDashboard from './StorageDashboard';
import BetriebsspiegelView from './BetriebsspiegelView';
import FieldComparisonView from './FieldComparisonView';
import WeeklyPlan from './WeeklyPlan';
import ActivitiesTable from './ActivitiesTable';
import FieldWeatherCard from './FieldWeatherCard';
import ComplianceDetail from './ComplianceDetail';
import AnalyticsDashboard from './AnalyticsDashboard';
import ProcurementView from './ProcurementView';
import DeliveryTracking from './DeliveryTracking';
import ContractsDetail from '../views/contracts/ContractsDetail';
import EnergyDetail from '../views/energy/EnergyDetail';
import MarketDetailWithWatchlist from '../views/market/MarketDetailWithWatchlist';
import SubsidyDetail from '../views/subsidies/SubsidyDetail';
import WeatherDetail from '../views/weather/WeatherDetail';
import HerdDetail from '../views/herd/HerdDetail';
import CashFlowDetail from '../views/cashflow/CashFlowDetail';
import FinanceDetail from '../views/finance/FinanceDetail';
import { useConfirmDialog } from './overlay/ConfirmDialog';
import { getCropColor } from '../config/cropColors';
import {
  XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ComposedChart, Bar,
  ReferenceLine,
} from 'recharts';

/**
 * Detail/Context page — shows contextual detail when user taps a dashboard card.
 * Page 2 (swipe left from chat).
 */
export default function DetailPage({ detailType, detailParams, weather, subsidies, sprayWindows, fieldwork, market, finance, cashflow, token, goToChat, isDesktop }) {
  const { t } = useTranslation();

  const HEADER_H = 64; // px

  // Fields uses the new unified FieldsView with its own action bar
  if (detailType === 'fields') {
    const FieldsView = React.lazy(() => import('../views/FieldsView'));
    return (
      <React.Suspense fallback={<div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" /></div>}>
        <FieldsView token={token} initialFieldId={detailParams?.fieldId} initialTab={detailParams?.tab} onOpenMap={detailParams?.onOpenMap} />
      </React.Suspense>
    );
  }

  if (!detailType) {
    return (
      <div className="relative w-full h-full overflow-hidden bg-[var(--aif-bg-page)]">
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <div className="w-14 h-14 bg-[var(--aif-bg-surface)] rounded-2xl flex items-center justify-center mb-4 border border-[var(--aif-border-default)]">
            <span className="text-2xl">📋</span>
          </div>
          <h2 className="text-lg font-semibold text-[var(--aif-text-primary)] mb-2">
            {t('detail.emptyTitle')}
          </h2>
          <p className="text-sm text-[var(--aif-text-muted)] max-w-xs leading-relaxed">
            {t('detail.emptyHint')}
          </p>
        </div>
      </div>
    );
  }

  const views = {
    weather: <WeatherDetail data={weather} sprayWindows={sprayWindows} fieldwork={fieldwork} token={token} />,
    subsidies: <SubsidyDetail data={subsidies} token={token} />,
    fields: <FieldsDetail token={token} initialFieldId={detailParams?.fieldId} initialTab={detailParams?.tab} />,
    market: <MarketDetailWithWatchlist market={market} token={token} />,
    finance: <FinanceDetail finance={finance} token={token} />,
    cashflow: <CashFlowDetail cashflow={cashflow} token={token} />,
    contracts: <ContractsDetail token={token} />,
    inventory: <InventoryDashboard token={token} />,
    storage: <StorageDashboard token={token} />,
    betriebsspiegel: <BetriebsspiegelView token={token} />,
    herd: <HerdDetail token={token} finance={finance} />,
    energy: <EnergyDetail token={token} finance={finance} />,
    compliance: <ComplianceDetail token={token} />,
    analytics: <AnalyticsDashboard token={token} />,
    procurement: <ProcurementView token={token} />,
    deliveries: <DeliveryTracking token={token} />,
    weekly: <WeeklyPlan token={token} preselectedFieldId={detailParams?.fieldId} />,
    activities_master: <ActivitiesTable token={token} />,
  };

  return (
    <div className="relative w-full h-full overflow-hidden bg-[var(--aif-bg-page)]">
      {/* ── Header (absolute top) ── */}
      <div
        className="absolute top-0 left-0 right-0 z-10 bg-[var(--aif-bg-page)]"
        style={{ height: HEADER_H }}
      >
        <div className="flex items-center gap-3 px-6 pt-6 pb-4">
          {!isDesktop && (
            <button
              onClick={goToChat}
              className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </button>
          )}
          <h1 className="text-lg font-semibold text-[var(--aif-text-primary)]">
            {t(`detail.header.${detailType}`, { defaultValue: t(`dashboard.${detailType}`, detailType) })}
          </h1>
        </div>
      </div>

      {/* ── Content (absolute middle, scrollable) ── */}
      <div
        className="absolute left-0 right-0 bottom-0 overflow-y-auto overscroll-contain px-6 pb-8"
        style={{ top: HEADER_H }}
      >
        {views[detailType] || null}
      </div>
    </div>
  );
}

// ─── Tooltip styling shared by all charts ─────────────────────
const TOOLTIP_STYLE = {
  background: 'var(--aif-bg-elevated)',
  border: '1px solid var(--aif-border-default)',
  borderRadius: 12,
  fontSize: 12,
};
const AXIS_TICK = { fontSize: 11, fill: 'var(--aif-text-muted)' };

// WeatherDetail + WeatherHistoryCard + formatWindowTime extracted to views/weather/WeatherDetail.jsx (A12 Sprint 5)

// SubsidyDetail extracted to views/subsidies/SubsidyDetail.jsx (A12 Sprint 4)

// ─── Fields Detail (Sprint 18a — enhanced) ─────────────────────
// ─── FieldsDetail (Sprint 33 — per-field tabs) ───────────────
function FieldsDetail({ token, initialFieldId, initialTab }) {
  const { t } = useTranslation();
  const [fields, setFields] = useState(null);
  const [selectedFieldId, setSelectedFieldId] = useState(initialFieldId || null);
  const [fieldDetail, setFieldDetail] = useState(null);
  const [fieldDetail52, setFieldDetail52] = useState(null);
  const [yieldData, setYieldData] = useState(null);
  const [rotation, setRotation] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab || 'overview');
  const [showComparison, setShowComparison] = useState(false);

  // Sync when navigated to a specific field (e.g. from map "Details" button)
  useEffect(() => {
    if (initialFieldId) setSelectedFieldId(initialFieldId);
  }, [initialFieldId]);

  // Sync tab when directed to a specific tab (e.g. from "Letzte Maßnahmen" kachel)
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (!token) return;
    apiClient.get('/api/v1/aif/farm/fields')
      .then(r => {
        const d = r.data;
        setFields(d);
        if (d?.fields?.length > 0 && !selectedFieldId) {
          setSelectedFieldId(initialFieldId || d.fields[0].id);
        }
      })
      .catch(() => {});
    apiClient.get('/api/v1/aif/farm/fruchtfolge')
      .then(r => setRotation(r.data)).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token || !selectedFieldId) return;
    apiClient.get(`/api/v1/aif/farm/fields/${selectedFieldId}`)
      .then(r => setFieldDetail(r.data)).catch(() => {});
    apiClient.get(`/api/v1/aif/farm/fields/${selectedFieldId}/yields`)
      .then(r => setYieldData(r.data)).catch(() => {});
    apiClient.get(`/api/v1/aif/farm/fields/${selectedFieldId}/detail`)
      .then(r => setFieldDetail52(r.data)).catch(() => {});
  }, [token, selectedFieldId]);

  const refreshYields = () => {
    if (!token || !selectedFieldId) return;
    apiClient.get(`/api/v1/aif/farm/fields/${selectedFieldId}/yields`)
      .then(r => setYieldData(r.data)).catch(() => {});
    apiClient.get(`/api/v1/aif/farm/fields/${selectedFieldId}/detail`)
      .then(r => setFieldDetail52(r.data)).catch(() => {});
  };

  if (!fields || !fields.fields?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center px-4">
        <div className="text-4xl mb-4">🌾</div>
        <h3 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-2">{t('dashboard.fields')}</h3>
        <p className="text-sm text-[var(--aif-text-muted)] max-w-[250px] leading-relaxed mb-4">{t('detail.noFields')}</p>
        <div className="text-xs text-[var(--aif-text-muted)]">{t('detail.fieldsHintUpload')}</div>
      </div>
    );
  }

  const totalHa = fields.fields.reduce((s, f) => s + (f.area_ha || 0), 0);
  const cropSet = new Set(fields.fields.map(f => f.current_crop_name).filter(Boolean));
  const gloez7Issues = rotation?.gloez7_issues || [];

  const TABS = [
    { key: 'overview',    label: t('field.fieldTabOverview'),    icon: '🌾' },
    { key: 'yield',       label: t('field.fieldTabYield'),       icon: '📊' },
    { key: 'rotation',    label: t('field.fieldTabRotation'),    icon: '🔄' },
    { key: 'economics',   label: t('field.fieldTabEconomics'),   icon: '💶' },
    { key: 'soil',        label: t('field.fieldTabSoil'),        icon: '🌍' },
    { key: 'activities',  label: t('field.fieldTabActivities'),  icon: '🚜' },
  ];

  return (
    <div className="space-y-3">
      {/* Compact farm summary */}
      <div className="flex items-center justify-between px-1">
        <div className="text-xs text-[var(--aif-text-secondary)]">
          {fields.count} {t('dashboard.fieldCount')} · {totalHa.toFixed(0)} ha · {cropSet.size} {t('dashboard.crops')}
          {gloez7Issues.length > 0 && <span className="ml-2 text-amber-400">⚠️ GLÖZ 7</span>}
        </div>
      </div>

      {/* View mode: single field vs portfolio comparison */}
      <div className="flex gap-2 justify-center">
        <button
          onClick={() => setShowComparison(false)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            !showComparison
              ? 'bg-[var(--aif-color-primary)] text-white'
              : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)]'
          }`}
        >
          {t('comparison.singleField')}
        </button>
        <button
          onClick={() => setShowComparison(true)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            showComparison
              ? 'bg-[var(--aif-color-primary)] text-white'
              : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)]'
          }`}
        >
          {t('comparison.portfolioView')}
        </button>
      </div>

      {/* Portfolio comparison view */}
      {showComparison && <FieldComparisonView token={token} />}

      {/* Field selector + tabs (single-field view) */}
      {!showComparison && (<>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {fields.fields.map(f => (
          <button
            key={f.id}
            onClick={() => setSelectedFieldId(f.id)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
              selectedFieldId === f.id
                ? 'bg-[var(--aif-color-primary)] text-white border-[var(--aif-color-primary)]'
                : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-secondary)] border-[var(--aif-border-default)]'
            }`}
          >
            {f.name} <span className="opacity-70">{f.area_ha} ha</span>
          </button>
        ))}
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[11px] font-medium border transition-all ${
              activeTab === tab.key
                ? 'bg-[var(--aif-color-primary)]/20 border-[var(--aif-color-primary)]/50 text-[var(--aif-color-primary)]'
                : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)] border-[var(--aif-border-default)]'
            }`}
          >
            <span>{tab.icon}</span><span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ── Overview Tab ── */}
      {activeTab === 'overview' && (
        <FieldOverviewTab field={fieldDetail || fields.fields.find(f => f.id === selectedFieldId)} fieldDetail52={fieldDetail52} t={t} gloez7Issues={gloez7Issues} token={token} />
      )}

      {/* ── Yield Tab ── */}
      {activeTab === 'yield' && (
        <FieldYieldTab
          fieldId={selectedFieldId}
          yieldData={yieldData}
          fieldDetail={fieldDetail}
          fieldDetail52={fieldDetail52}
          token={token}
          t={t}
          onSaved={refreshYields}
        />
      )}

      {/* ── Rotation Tab ── */}
      {activeTab === 'rotation' && (
        <FieldRotationTab
          fieldId={selectedFieldId}
          fieldName={(fieldDetail || fields.fields.find(f => f.id === selectedFieldId))?.name}
          rotation={rotation}
          fieldDetail52={fieldDetail52}
          t={t}
        />
      )}

      {/* ── Economics Tab ── */}
      {activeTab === 'economics' && (
        <FieldEconomicsTab fieldId={selectedFieldId} token={token} t={t} />
      )}

      {/* ── Soil Tab ── */}
      {activeTab === 'soil' && (
        <FieldSoilTab field={fieldDetail} fieldDetail52={fieldDetail52} token={token} t={t} selectedFieldId={selectedFieldId} onSaved={() => {
          apiClient.get(`/api/v1/aif/farm/fields/${selectedFieldId}`)
            .then(r => setFieldDetail(r.data)).catch(() => {});
          apiClient.get(`/api/v1/aif/farm/fields/${selectedFieldId}/detail`)
            .then(r => setFieldDetail52(r.data)).catch(() => {});
        }} />
      )}

      {/* ── Activities Tab ── */}
      {activeTab === 'activities' && (
        <FieldActivitiesTab fieldId={selectedFieldId} token={token} t={t} />
      )}
      </>)}
    </div>
  );
}

// ─── Field Overview Tab ───────────────────────────────────────
function FieldOverviewTab({ field, fieldDetail52, t, gloez7Issues, token }) {
  // Hooks must be called before any early return
  const [quickStats, setQuickStats] = useState({ nApplied: null, nLimit: 170, sprayOk: null, nextTask: null });
  useEffect(() => {
    if (!token || !field.id) return;
    // N-balance: sum nitrogen from activities on this field
    apiClient.get(`/api/v1/aif/farm/fields/${field.id}/activities?limit=50`)
      .then(r => {
        const d = r.data;
        const acts = d?.activities || (Array.isArray(d) ? d : []);
        const nSum = acts.reduce((s, a) => s + (a.nutrient_n_kg_ha || 0), 0);
        if (nSum > 0) setQuickStats(prev => ({ ...prev, nApplied: Math.round(nSum) }));
      })
      .catch(() => {});
    // Spray status: use per-field weather
    if (field.latitude) {
      apiClient.post(`/api/v1/aif/weather/fields`, { field_ids: [field.id] }, {
        entityType: 'weather', queueOnOffline: false,
      })
        .then(r => r?.data ?? null)
        .then(d => {
          const fw = d?.fields?.[0] || d;
          if (fw?.spray_ok != null) setQuickStats(prev => ({ ...prev, sprayOk: fw.spray_ok }));
        })
        .catch(() => {});
    }
    // Next task on this field
    apiClient.get(`/api/v1/aif/farm/tasks?field_id=${field.id}&status=DRAFT,SUBMITTED&limit=1`)
      .then(r => {
        const d = r.data;
        const tasks = d?.tasks || (Array.isArray(d) ? d : []);
        if (tasks.length > 0) {
          const nt = tasks[0];
          const due = nt.due_date ? new Date(nt.due_date) : null;
          const today = new Date(); today.setHours(0,0,0,0);
          let dueLabel = '—';
          if (due) {
            const diff = Math.round((due - today) / 86400000);
            if (diff === 0) dueLabel = t('dashboard.today') || 'heute';
            else if (diff === 1) dueLabel = t('dashboard.tomorrow') || 'morgen';
            else if (diff > 0) dueLabel = `${diff}${t('time.daysShort')}`;
            else dueLabel = t('today.overdue');
          }
          setQuickStats(prev => ({ ...prev, nextTask: { title: nt.title, due: dueLabel } }));
        }
      })
      .catch(() => {});
  }, [token, field?.id]);

  if (!field) return null;
  const fieldIssue = gloez7Issues.find(i => i.field_id === field.id);
  const d52 = fieldDetail52;
  const gloez = d52?.gloez_status || fieldIssue ? (d52?.gloez_status || { severity: fieldIssue?.severity, message: fieldIssue?.message }) : null;

  const formatEur = (v) => v != null ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) + ' €' : '—';

  // Translate crop name
  const cropDisplay = field.current_crop_name ? t(`crops.${field.current_crop_name}`, { defaultValue: field.current_crop_name }) : null;

  return (
    <div className="space-y-3">
      {/* Context Header — WHERE am I, what are the key facts */}
      <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-semibold text-[var(--aif-text-primary)]">{field.name}</h3>
          <span className="text-sm font-medium text-[var(--aif-text-secondary)]">{field.area_ha} ha</span>
        </div>
        {/* One-line summary: crop + soil + BP */}
        <div className="text-xs text-[var(--aif-text-muted)] flex items-center gap-1.5 flex-wrap">
          {cropDisplay && <span>🌾 {cropDisplay}{field.current_variety ? ` (${field.current_variety})` : ''}</span>}
          {cropDisplay && field.soil_type && <span>·</span>}
          {field.soil_type && <span>{field.soil_type}</span>}
          {field.soil_quality_index && <span>· {field.soil_quality_index} BP</span>}
        </div>

        {/* Quick Stats Bar — what's HAPPENING now */}
        <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-[var(--aif-border-default)]">
          {/* N-Bilanz */}
          <div className="text-center">
            <div className="text-[10px] text-[var(--aif-text-muted)] mb-0.5">{t('field.nBalance')}</div>
            {quickStats.nApplied != null ? (
              <>
                <div className="text-sm font-semibold text-[var(--aif-text-primary)]">
                  {quickStats.nApplied}/{quickStats.nLimit}
                  <span className="ml-1">{quickStats.nApplied <= quickStats.nLimit ? '✅' : '⚠️'}</span>
                </div>
                <div className="text-[10px] text-[var(--aif-text-muted)]">
                  {Math.round((quickStats.nApplied / quickStats.nLimit) * 100)}% {t('field.nUsed')}
                </div>
              </>
            ) : (
              <div className="text-xs text-[var(--aif-text-muted)]">—</div>
            )}
          </div>
          {/* Spray Status */}
          <div className="text-center">
            <div className="text-[10px] text-[var(--aif-text-muted)] mb-0.5">{t('weather.sprayTitle')}</div>
            {quickStats.sprayOk != null ? (
              <div className={`text-sm font-semibold ${quickStats.sprayOk ? 'text-green-400' : 'text-red-400'}`}>
                {quickStats.sprayOk ? '🟢' : '🔴'} {quickStats.sprayOk ? t('weather.sprayOpen') : t('weather.sprayClosed')}
              </div>
            ) : (
              <div className="text-xs text-[var(--aif-text-muted)]">—</div>
            )}
          </div>
          {/* Nächste Aufgabe */}
          <div className="text-center">
            <div className="text-[10px] text-[var(--aif-text-muted)] mb-0.5">{t('field.nextTask')}</div>
            {quickStats.nextTask ? (
              <>
                <div className="text-sm font-semibold text-[var(--aif-text-primary)] truncate">{quickStats.nextTask.due}</div>
                <div className="text-[10px] text-[var(--aif-text-muted)] truncate">{quickStats.nextTask.title}</div>
              </>
            ) : (
              <div className="text-xs text-[var(--aif-text-muted)]">{t('field.noTasks')}</div>
            )}
          </div>
        </div>
      </div>

      {/* Feldbestandswert — compact inline, not hero */}
      {d52?.field_value_eur > 0 && (
        <div className="flex items-center justify-between px-1 py-1 text-xs">
          <span className="text-[var(--aif-text-muted)]">
            {t('field.fieldValue')}: <span className="font-semibold text-[var(--aif-color-primary)]">{formatEur(d52.field_value_eur)}</span>
          </span>
          {d52.days_to_harvest != null && (
            <span className="text-[var(--aif-text-muted)]">🌾 {d52.days_to_harvest} {t('field.daysToHarvest')}</span>
          )}
        </div>
      )}

      {/* Letzte Maßnahme — what HAPPENED recently */}
      <LastActivitySection fieldId={field.id} token={token} t={t} />

      {/* Badges — nitrate zone, organic, etc. */}
      {(field.nitrate_zone || field.water_protection_zone || field.organic_field || field.drainage || field.irrigation) && (
        <div className="flex flex-wrap gap-1.5">
          {field.nitrate_zone && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">⚠️ {t('field.soilNitrateZone')}</span>}
          {field.water_protection_zone && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">💧 {t('field.soilWaterProtection')}</span>}
          {field.organic_field && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">🌿 {t('field.soilOrganic')}</span>}
          {field.drainage && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)]">🌊 {t('field.soilDrainage')}</span>}
          {field.irrigation && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)]">💦 {t('field.soilIrrigation')}</span>}
        </div>
      )}

      {/* FLIK / Registration data */}
      {d52 && (d52.flik_number || d52.gemarkung || d52.flurstueck) && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 text-xs space-y-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--aif-text-muted)] mb-2">{t('field.fieldRegistration')}</div>
          {d52.flik_number && (
            <div className="flex justify-between">
              <span className="text-[var(--aif-text-muted)]">{t('field.flikNumber')}</span>
              <span className="text-[var(--aif-text-primary)] font-mono">{d52.flik_number}</span>
            </div>
          )}
          {d52.gemarkung && (
            <div className="flex justify-between">
              <span className="text-[var(--aif-text-muted)]">{t('field.gemarkung')}</span>
              <span className="text-[var(--aif-text-primary)]">{d52.gemarkung}</span>
            </div>
          )}
          {d52.flurstueck && (
            <div className="flex justify-between">
              <span className="text-[var(--aif-text-muted)]">{t('field.flurstueck')}</span>
              <span className="text-[var(--aif-text-primary)]">{d52.flurstueck}</span>
            </div>
          )}
        </div>
      )}

      {/* Ownership / Pacht */}
      {d52?.ownership_type && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 text-xs space-y-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--aif-text-muted)] mb-2">{t('field.ownershipType')}</div>
          <div className="flex justify-between">
            <span className="text-[var(--aif-text-muted)]">{t('field.ownershipType')}</span>
            <span className={`font-medium ${d52.ownership_type === 'OWNED' ? 'text-green-400' : 'text-amber-400'}`}>
              {d52.ownership_type === 'OWNED' ? t('field.ownershipOwned') : t('field.ownershipLeased')}
            </span>
          </div>
          {d52.pacht_rate_eur_ha != null && (
            <div className="flex justify-between">
              <span className="text-[var(--aif-text-muted)]">{t('field.pachtRate')}</span>
              <span className="text-[var(--aif-text-primary)]">{d52.pacht_rate_eur_ha.toLocaleString()} EUR/ha</span>
            </div>
          )}
          {d52.pacht_contract_end && (
            <div className="flex justify-between">
              <span className="text-[var(--aif-text-muted)]">{t('field.pachtEnd')}</span>
              <span className="text-[var(--aif-text-primary)]">{d52.pacht_contract_end}</span>
            </div>
          )}
          {d52.pacht_notice_months != null && (
            <div className="flex justify-between">
              <span className="text-[var(--aif-text-muted)]">{t('field.pachtNotice')}</span>
              <span className="text-[var(--aif-text-primary)]">{d52.pacht_notice_months} {t('field.pachtMonths')}</span>
            </div>
          )}
        </div>
      )}

      {/* GLÖZ 7 status */}
      {gloez && (
        <div className={`rounded-xl border p-3 text-xs ${gloez.severity === 'error' ? 'border-red-500/30 bg-red-500/5 text-red-400' : 'border-amber-500/30 bg-amber-500/5 text-amber-400'}`}>
          {gloez.severity === 'error' ? '❌' : '⚠️'} {gloez.message?.replace(/\((\w+)\)/, (_, crop) => `(${t(`crops.${crop}`, { defaultValue: crop })})`) || gloez.message}
        </div>
      )}

      {/* Per-field weather + GDD — only when GPS is set */}
      {field.latitude && token && (
        <div className="space-y-1">
          <div className="text-xs font-medium text-[var(--aif-text-secondary)] px-1">{t('weather.fieldWeatherTitle')}</div>
          <FieldWeatherCard fieldId={field.id} token={token} />
        </div>
      )}
    </div>
  );
}

/**
 * LastActivitySection — shows the most recent activity for a field.
 * Answers: "what was the last thing done on this field?"
 */
function LastActivitySection({ fieldId, token, t }) {
  const [activity, setActivity] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!token || !fieldId) return;
    apiClient.get(`/api/v1/aif/farm/fields/${fieldId}/activities?limit=1`)
      .then(r => {
        const d = r.data;
        if (d?.activities?.length > 0) setActivity(d.activities[0]);
        else if (Array.isArray(d) && d.length > 0) setActivity(d[0]);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [token, fieldId]);

  if (!loaded) return null;

  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-[var(--aif-text-secondary)] px-1">{t('field.lastActivity')}</div>
      {activity ? (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] px-3 py-2.5 text-xs flex items-center gap-2">
          <span className="text-[var(--aif-text-muted)]">
            {activity.date ? new Date(activity.date).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
          </span>
          <span className="text-[var(--aif-text-primary)] font-medium">{activity.activity_type_label || t(`activityType.${activity.activity_type}`, { defaultValue: activity.activity_type }) || '—'}</span>
          {activity.product_name && <span className="text-[var(--aif-text-muted)]">· {activity.product_name} {activity.quantity ? `${activity.quantity} ${activity.unit || ''}` : ''}</span>}
        </div>
      ) : (
        <div className="text-xs text-[var(--aif-text-muted)] px-1">{t('field.noActivities')}</div>
      )}
    </div>
  );
}

// ─── Field Yield Tab ──────────────────────────────────────────
function FieldYieldTab({ fieldId, yieldData, fieldDetail, fieldDetail52, token, t, onSaved }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ year: new Date().getFullYear(), crop_code: 'wheat', yield_qty: '', revenue_per_unit: '', variety: '', moisture_pct: '', protein_pct: '' });
  const [saving, setSaving] = useState(false);

  const CROP_OPTIONS = ['wheat','barley','rapeseed','corn','sugarbeet','potato','rye','oats','grassland'];

  const handleSave = async () => {
    if (!token || !fieldId || !form.crop_code) return;
    setSaving(true);
    const payload = { crop_code: form.crop_code, year: parseInt(form.year) };
    if (form.yield_qty) payload.yield_qty = parseFloat(form.yield_qty);
    if (form.revenue_per_unit) payload.revenue_per_unit = parseFloat(form.revenue_per_unit);
    if (form.variety) payload.variety = form.variety;
    if (form.moisture_pct) payload.moisture_pct = parseFloat(form.moisture_pct);
    if (form.protein_pct) payload.protein_pct = parseFloat(form.protein_pct);
    try {
      await apiClient.post(`/api/v1/aif/farm/fields/${fieldId}/yields`, payload, {
        entityType: 'yield', queueOnOffline: true,
      });
      setShowForm(false); onSaved?.();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    setSaving(false);
  };

  const records = yieldData?.records || [];
  const chartData = [...records].reverse()
    .map(r => ({ name: String(r.year), yield: r.yield_qty, revenue: r.revenue_per_unit }))
    .filter(r => r.yield);

  const yieldStats = fieldDetail52?.yield_stats;
  const ktbl = fieldDetail52?.ktbl_benchmark;
  const TREND_ICON = { improving: '↑', declining: '↓', stable: '→' };
  const TREND_COLOR = { improving: 'text-green-400', declining: 'text-red-400', stable: 'text-[var(--aif-text-muted)]' };

  return (
    <div className="space-y-4">
      {/* Summary */}
      {yieldData?.avg_yield_t_ha && (
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] p-3 text-center">
            <div className="text-xl font-bold text-[var(--aif-color-primary)]">{yieldData.avg_yield_t_ha}</div>
            <div className="text-[10px] text-[var(--aif-text-muted)]">{t('field.yieldAvg')} t/ha</div>
          </div>
          {yieldData.total_revenue > 0 && (
            <div className="rounded-xl bg-green-500/10 border border-green-500/30 p-3 text-center">
              <div className="text-lg font-bold text-green-400">{yieldData.total_revenue?.toLocaleString(undefined, {maximumFractionDigits:0})}</div>
              <div className="text-[10px] text-[var(--aif-text-muted)]">{t('field.yieldRevenueTotal')} EUR</div>
            </div>
          )}
        </div>
      )}

      {/* Trend + 5yr stats from Sprint 52 detail */}
      {yieldStats?.avg_5yr != null && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 text-xs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--aif-text-muted)]">{t('field.yieldTrend')}</span>
            {yieldStats.trend && (
              <span className={`text-sm font-bold ${TREND_COLOR[yieldStats.trend]}`}>
                {TREND_ICON[yieldStats.trend]} {t(`field.yieldTrend${yieldStats.trend.charAt(0).toUpperCase() + yieldStats.trend.slice(1)}`)}
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="font-bold text-[var(--aif-color-primary)]">{yieldStats.avg_5yr}</div>
              <div className="text-[9px] text-[var(--aif-text-muted)]">{t('field.avg5yr')}</div>
            </div>
            {yieldStats.best_year && (
              <div>
                <div className="font-bold text-green-400">{yieldStats.best_year.yield_qty}</div>
                <div className="text-[9px] text-[var(--aif-text-muted)]">{t('field.bestYear', { year: yieldStats.best_year.year })}</div>
              </div>
            )}
            {yieldStats.worst_year && (
              <div>
                <div className="font-bold text-amber-400">{yieldStats.worst_year.yield_qty}</div>
                <div className="text-[9px] text-[var(--aif-text-muted)]">{t('field.minYear', { year: yieldStats.worst_year.year })}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bar chart with KTBL reference line */}
      {chartData.length >= 2 && (
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--aif-border-default)" />
              <XAxis dataKey="name" tick={AXIS_TICK} />
              <YAxis tick={AXIS_TICK} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="yield" fill="var(--aif-color-primary)" radius={[4,4,0,0]} name="t/ha" />
              {ktbl?.reference && (
                <ReferenceLine y={ktbl.reference} stroke="#6b7280" strokeDasharray="4 2"
                  label={{ value: `KTBL ${ktbl.reference}`, fontSize: 9, fill: '#6b7280', position: 'right' }} />
              )}
              {ktbl?.top25 && (
                <ReferenceLine y={ktbl.top25} stroke="#10b981" strokeDasharray="4 2"
                  label={{ value: `Top25 ${ktbl.top25}`, fontSize: 9, fill: '#10b981', position: 'right' }} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* KTBL benchmark summary panel */}
      {ktbl && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 text-xs">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--aif-text-muted)] mb-2">{t('field.ktblBenchmark')}</div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="font-bold text-[var(--aif-text-muted)]">{ktbl.reference}</div>
              <div className="text-[9px] text-[var(--aif-text-muted)]">{t('field.ktblReference')}</div>
            </div>
            <div>
              <div className="font-bold text-green-400">{ktbl.top25}</div>
              <div className="text-[9px] text-[var(--aif-text-muted)]">{t('field.ktblTop25')}</div>
            </div>
            <div>
              <div className={`font-bold ${ktbl.vs_reference_pct >= 0 ? 'text-[var(--aif-color-primary)]' : 'text-amber-400'}`}>{ktbl.your_5yr_avg}</div>
              <div className="text-[9px] text-[var(--aif-text-muted)]">{t('field.ktblYourAvg')}</div>
            </div>
          </div>
          {ktbl.vs_reference_pct != null && (
            <div className={`text-center text-[10px] mt-1.5 font-medium ${ktbl.vs_reference_pct >= 0 ? 'text-green-400' : 'text-amber-400'}`}>
              {ktbl.vs_reference_pct > 0 ? '+' : ''}{ktbl.vs_reference_pct}% {t('field.ktblVsRef')}
            </div>
          )}
        </div>
      )}

      {/* Add button */}
      <button onClick={() => setShowForm(!showForm)}
        className="w-full py-2 rounded-xl text-xs font-medium border border-dashed border-[var(--aif-color-primary)]/50 text-[var(--aif-color-primary)] hover:bg-[var(--aif-color-primary)]/5">
        + {t('field.yieldAdd')}
      </button>

      {/* Form */}
      {showForm && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4 space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-[var(--aif-text-muted)]">{t('field.yieldYear')}</label>
              <input type="number" value={form.year} onChange={e => setForm({...form, year: e.target.value})}
                className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none" />
            </div>
            <div>
              <label className="text-[10px] text-[var(--aif-text-muted)]">{t('field.yieldCrop')}</label>
              <select value={form.crop_code} onChange={e => setForm({...form, crop_code: e.target.value})}
                className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none">
                {CROP_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          {[
            ['yield_qty', `${t('field.yieldQty')} (t/ha)`, '8.2'],
            ['revenue_per_unit', `${t('field.yieldRevenue')} (EUR/t)`, '235'],
            ['variety', t('field.yieldVariety'), 'z.B. Benchmark'],
            ['moisture_pct', `${t('field.yieldMoisture')} (%)`, '13.5'],
          ].map(([key, label, placeholder]) => (
            <div key={key}>
              <label className="text-[10px] text-[var(--aif-text-muted)]">{label}</label>
              <input type={key === 'variety' ? 'text' : 'number'} step="0.1" placeholder={placeholder}
                value={form[key]} onChange={e => setForm({...form, [key]: e.target.value})}
                className="w-full mt-0.5 px-2 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-primary)] outline-none" />
            </div>
          ))}
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving}
              className="flex-1 py-2 rounded-xl bg-[var(--aif-color-primary)] text-white text-xs font-medium">
              {saving ? '...' : t('field.yieldSave')}
            </button>
            <button onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-xl border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-muted)]">
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Records table */}
      {records.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-[var(--aif-border-default)]">
          <table className="w-full text-[11px] min-w-[350px]">
            <thead>
              <tr className="bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] uppercase text-[9px]">
                <th className="text-left p-2 pl-3">{t('field.yieldYear')}</th>
                <th className="text-left p-2">{t('field.yieldCrop')}</th>
                <th className="text-right p-2">t/ha</th>
                <th className="text-right p-2">{t('field.yieldRevenue')}</th>
                <th className="text-right p-2 pr-3">{t('finance.revenue')}</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={i} className="border-t border-[var(--aif-border-default)]">
                  <td className="p-2 pl-3 text-[var(--aif-text-primary)] font-medium">{r.year}</td>
                  <td className="p-2 text-[var(--aif-text-muted)]">{r.crop_name || r.crop_code}</td>
                  <td className="text-right p-2 text-[var(--aif-text-primary)]">{r.yield_qty ?? '—'}</td>
                  <td className="text-right p-2 text-[var(--aif-text-muted)]">{r.revenue_per_unit ?? '—'}</td>
                  <td className="text-right p-2 pr-3 text-green-400">{r.revenue_total?.toLocaleString(undefined, {maximumFractionDigits:0}) ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !showForm ? (
        <p className="text-xs text-[var(--aif-text-muted)] text-center py-4">{t('field.yieldNoData')}</p>
      ) : null}
    </div>
  );
}

// ─── Field Rotation Tab ───────────────────────────────────────

function FieldRotationTab({ fieldId, fieldName, rotation, fieldDetail52, t }) {
  if (!rotation) return <div className="text-xs text-[var(--aif-text-muted)] text-center py-8">{t('dashboard.loading')}</div>;

  const currentYear = rotation.current_year || new Date().getFullYear();
  const years = rotation.years || [];
  const displayYears = years.filter(y => y >= currentYear - 4 && y <= currentYear + 2);
  const fieldRow = rotation.fields?.find(f => f.field_id === fieldId);
  const fieldIssue = rotation.gloez7_issues?.find(i => i.field_id === fieldId);

  // Crop diversity: unique crops in last 5 years for this field
  const recentYears = Object.entries(fieldRow?.years || {})
    .filter(([yr]) => parseInt(yr) >= currentYear - 4 && parseInt(yr) <= currentYear)
    .map(([, cell]) => cell?.crop_code).filter(Boolean);
  const uniqueCrops = new Set(recentYears).size;

  if (!fieldRow) return <div className="text-xs text-[var(--aif-text-muted)] text-center py-8">{t('field.yieldNoData')}</div>;

  return (
    <div className="space-y-4">
      {/* GLÖZ 7 status */}
      {fieldIssue ? (
        <div className={`rounded-xl border p-3 text-xs ${fieldIssue.severity === 'error' ? 'border-red-500/30 bg-red-500/5 text-red-400' : 'border-amber-500/30 bg-amber-500/5 text-amber-400'}`}>
          {fieldIssue.severity === 'error' ? t('field.rotationGloez7Error') : t('field.rotationGloez7Warning')}: {fieldIssue.message}
        </div>
      ) : (
        <div className="rounded-xl border border-green-500/30 bg-green-500/5 p-3 text-xs text-green-400">
          {t('field.rotationGloez7Ok')}
        </div>
      )}

      {/* Crop diversity score */}
      {uniqueCrops > 0 && (
        <div className="flex items-center justify-between rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] px-3 py-2 text-xs">
          <span className="text-[var(--aif-text-muted)]">{t('field.cropDiversity')} (5 J.)</span>
          <span className={`font-semibold ${uniqueCrops >= 3 ? 'text-green-400' : uniqueCrops === 2 ? 'text-amber-400' : 'text-red-400'}`}>
            {uniqueCrops} {t('dashboard.crops')} {uniqueCrops >= 3 ? '✅' : uniqueCrops === 2 ? '⚠️' : '❌'}
          </span>
        </div>
      )}

      {/* Timeline for this field */}
      <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide">{fieldName} — {t('field.rotationTitle')}</h3>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {displayYears.map(year => {
          const cell = fieldRow.years[String(year)];
          const bg = cell ? getCropColor(cell.crop_code).bg : 'transparent';
          const isFuture = year > currentYear;
          const isCurrent = year === currentYear;
          return (
            <div key={year} className={`flex-shrink-0 rounded-xl p-3 text-center min-w-[70px] border ${isCurrent ? 'border-[var(--aif-color-primary)] ring-1 ring-[var(--aif-color-primary)]/30' : 'border-[var(--aif-border-default)]'}`}
              style={{ backgroundColor: cell ? `${bg}22` : 'var(--aif-bg-surface)' }}>
              <div className={`text-[10px] font-bold mb-1 ${isCurrent ? 'text-[var(--aif-color-primary)]' : 'text-[var(--aif-text-muted)]'}`}>{year}</div>
              {cell ? (
                <>
                  <div className="text-xs font-medium text-[var(--aif-text-primary)] leading-tight" style={{ color: bg }}>{cell.crop_name}</div>
                  {cell.yield_qty && <div className="text-[10px] text-[var(--aif-text-muted)] mt-0.5">{cell.yield_qty} t/ha</div>}
                  {isFuture && <div className="text-[9px] text-[var(--aif-text-muted)] mt-0.5">{t('field.rotationFuture')}</div>}
                </>
              ) : (
                <div className="text-[10px] text-[var(--aif-text-muted)]">—</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Full farm rotation grid */}
      {rotation.fields?.length > 1 && (
        <div>
          <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide mb-2">{t('field.allFields')}</h3>
          <div className="overflow-x-auto rounded-xl border border-[var(--aif-border-default)]">
            <table className="w-full text-[11px] min-w-[400px]">
              <thead>
                <tr className="bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] uppercase text-[9px]">
                  <th className="text-left p-2 pl-3">{t('farm.fieldName')}</th>
                  {displayYears.map(y => (
                    <th key={y} className={`text-center p-2 ${y === currentYear ? 'text-[var(--aif-color-primary)]' : ''}`}>{y}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rotation.fields.map((row, i) => {
                  const rowIssue = rotation.gloez7_issues?.find(x => x.field_id === row.field_id);
                  return (
                    <tr key={i} className={`border-t border-[var(--aif-border-default)] ${row.field_id === fieldId ? 'bg-[var(--aif-color-primary)]/5' : ''}`}>
                      <td className="p-2 pl-3 text-[var(--aif-text-primary)] whitespace-nowrap">
                        {row.field_name}
                        {rowIssue && <span className="ml-1 text-[9px]">{rowIssue.severity === 'error' ? '❌' : '⚠️'}</span>}
                      </td>
                      {displayYears.map(year => {
                        const cell = row.years[String(year)];
                        const bg = cell ? getCropColor(cell.crop_code).bg : undefined;
                        return (
                          <td key={year} className="text-center p-2" style={bg ? { color: bg } : {}}>
                            {cell ? <span title={cell.crop_name}>{cell.crop_name?.slice(0,6)}{cell.yield_qty ? ` ${cell.yield_qty}` : ''}</span> : <span className="text-[var(--aif-text-muted)]">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sprint 57: Rotation optimizer hint */}
      <div className="rounded-xl border border-dashed border-green-500/30 bg-green-500/5 p-3 text-xs text-green-400 text-center">
        🌱 {t('rotation.fieldHint')}
      </div>
    </div>
  );
}

// ─── Field Economics Tab ──────────────────────────────────────
function FieldEconomicsTab({ fieldId, token, t }) {
  const [cmData, setCmData] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch('/api/v1/aif/finance/contribution-margin', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setCmData).catch(() => {});
  }, [token]);

  const fieldEntry = cmData?.per_field?.find(pf => pf.field_id === fieldId);
  const currency = cmData?.currency || 'EUR';

  if (!cmData) return <div className="text-xs text-[var(--aif-text-muted)] text-center py-8">{t('dashboard.loading')}</div>;
  if (!fieldEntry) return <div className="text-xs text-[var(--aif-text-muted)] text-center py-8">{t('field.noEconomicsData')}</div>;

  const ha = fieldEntry.per_ha || {};
  const vc = ha.variable_costs || {};
  const sources = ha.sources || {};

  // Field ranking among all fields
  const allDbI = (cmData.per_field || []).map(pf => pf.per_ha?.db_i || 0).sort((a,b) => b-a);
  const rank = allDbI.indexOf(ha.db_i) + 1;

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="rounded-xl bg-[var(--aif-color-primary)]/10 border border-[var(--aif-color-primary)]/30 p-4 text-center">
        <div className="text-3xl font-bold text-[var(--aif-color-primary)]">{ha.db_i?.toLocaleString()} {currency}/ha</div>
        <div className="text-xs text-[var(--aif-text-muted)] mt-1">{t('finance.dbI')} | {t('field.rankOf', { rank, total: cmData.per_field?.length })}</div>
        <div className="text-xs text-[var(--aif-text-muted)]">{t('finance.total')}: {fieldEntry.total?.db_i?.toLocaleString()} {currency} ({fieldEntry.area_ha} ha)</div>
      </div>

      {/* Revenue */}
      <div className="text-xs space-y-1.5">
        <div className="font-semibold text-green-400 uppercase text-[10px]">{t('finance.revenue')}/{t('finance.perHa')}</div>
        <div className="flex justify-between"><span className="text-[var(--aif-text-muted)]">{t('field.yieldQty')}: {ha.yield_t_ha} t/ha × {ha.market_price_t} EUR/t</span><span className="text-[var(--aif-text-primary)] flex items-center gap-1">{(ha.yield_t_ha * ha.market_price_t)?.toLocaleString()} <SourceBadge source={sources.yield || 'reference'} variant="compact" /><SourceBadge source={sources.price || 'reference'} variant="compact" /></span></div>
        <div className="flex justify-between"><span className="text-[var(--aif-text-muted)]">+ {t('finance.subsidy')}</span><span className="text-[var(--aif-text-primary)]">{ha.subsidy_per_ha?.toLocaleString()}</span></div>
        <div className="flex justify-between font-semibold border-t border-[var(--aif-border-default)] pt-1"><span className="text-green-400">= {t('finance.revenue')}</span><span className="text-green-400">{ha.revenue?.toLocaleString()}</span></div>
      </div>

      {/* Costs */}
      <div className="text-xs space-y-1.5">
        <div className="font-semibold text-red-400 uppercase text-[10px]">{t('finance.variableCosts')}/{t('finance.perHa')}</div>
        {[['seed',t('finance.seed')],['fertilizer',t('finance.fertilizer')],['plant_protection',t('finance.ppp')],['machinery_variable',t('finance.machinery')],['contractor',t('finance.contractor')]].map(([k,l]) => (
          vc[k] > 0 ? <div key={k} className="flex justify-between"><span className="text-[var(--aif-text-muted)]">{l}</span><span className="text-[var(--aif-text-primary)]">{vc[k]?.toLocaleString()}</span></div> : null
        ))}
        <div className="flex justify-between font-semibold border-t border-[var(--aif-border-default)] pt-1"><span className="text-red-400">= {t('finance.variableCosts')}</span><span className="text-red-400">{vc.total?.toLocaleString()}</span></div>
      </div>

      {/* User data pct */}
      {fieldEntry.user_data_pct > 0 && (
        <div className="text-[10px] text-[var(--aif-color-primary)] flex items-center gap-1">
          <SourceBadge source="user_input" variant="compact" /> {t('finance.userDataPct', { pct: fieldEntry.user_data_pct })}
        </div>
      )}
    </div>
  );
}

// ─── Field Soil Tab ───────────────────────────────────────────
function FieldSoilTab({ field, fieldDetail52, token, t, selectedFieldId, onSaved }) {
  const [editing, setEditing] = useState({});
  const [saved, setSaved] = useState(false);

  const handleSave = async (patchData) => {
    if (!token || !selectedFieldId) return;
    try {
      // TODO(5b): activate concurrency — pass opts.concurrency = { updatedAt, base } once caller threads originalState through
      await apiClient.patch(`/api/v1/aif/farm/fields/${selectedFieldId}`, patchData, {
        entityType: 'field', queueOnOffline: true,
      });
      setSaved(true); setTimeout(() => setSaved(false), 1500);
      onSaved?.();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    setEditing({});
  };

  const SOIL_TYPES = [t('field.soilSand'), t('field.soilLoam'), t('field.soilClay'), t('field.soilSilt'), t('field.soilPeat'), t('field.soilLoess'), t('field.soilSandyLoam'), t('field.soilClayLoam')];
  const NUTRIENT_LEVELS = ['A', 'B', 'C', 'D', 'E'];
  const NUTRIENT_LABELS = { A: t('field.nutrientA'), B: t('field.nutrientB'), C: t('field.nutrientC'), D: t('field.nutrientD'), E: t('field.nutrientE') };
  const NUTRIENT_COLORS = { A: 'text-red-400', B: 'text-amber-400', C: 'text-green-400', D: 'text-blue-400', E: 'text-purple-400' };

  if (!field) return <div className="text-xs text-[var(--aif-text-muted)] text-center py-8">{t('dashboard.loading')}</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide">{t('field.soilTitle')}</h3>
        {saved && <span className="text-[10px] text-green-400">✓ {t('profile.saved')}</span>}
      </div>

      {/* Soil type + quality */}
      <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 space-y-3 text-xs">
        <div className="flex justify-between items-center">
          <span className="text-[var(--aif-text-muted)]">{t('field.soilType')}</span>
          {editing.soil_type ? (
            <select autoFocus defaultValue={field.soil_type || ''} onBlur={e => handleSave({soil_type: e.target.value})}
              className="px-2 py-1 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-color-primary)] text-xs text-[var(--aif-text-primary)] outline-none">
              <option value="">{t('field.soilTypeSelect')}</option>
              {SOIL_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <button onClick={() => setEditing({soil_type: true})} className="group flex items-center gap-1">
              <span className={field.soil_type ? 'text-[var(--aif-text-primary)]' : 'text-[var(--aif-text-muted)]'}>{field.soil_type || t('ui.enterValue')}</span>
              <span className="opacity-0 group-hover:opacity-100 text-[10px] text-[var(--aif-color-primary)]">✎</span>
            </button>
          )}
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[var(--aif-text-muted)]">{t('field.soilQuality')} (0–100)</span>
          {editing.soil_quality_index ? (
            <input autoFocus type="number" min="0" max="100" defaultValue={field.soil_quality_index || ''}
              onBlur={e => handleSave({soil_quality_index: parseInt(e.target.value)})}
              onKeyDown={e => e.key === 'Enter' && handleSave({soil_quality_index: parseInt(e.target.value)})}
              className="w-20 text-right px-2 py-1 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-color-primary)] text-xs text-[var(--aif-text-primary)] outline-none" />
          ) : (
            <button onClick={() => setEditing({soil_quality_index: true})} className="group flex items-center gap-1">
              <span className={field.soil_quality_index ? 'text-[var(--aif-color-primary)] font-bold' : 'text-[var(--aif-text-muted)]'}>{field.soil_quality_index ?? t('ui.enterValue')}{field.soil_quality_index ? ' BP' : ''}</span>
              <span className="opacity-0 group-hover:opacity-100 text-[10px] text-[var(--aif-color-primary)]">✎</span>
            </button>
          )}
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[var(--aif-text-muted)]">{t('field.soilPh')}</span>
          {editing.ph_value ? (
            <input autoFocus type="number" step="0.1" min="4" max="9" defaultValue={field.ph_value || ''}
              onBlur={e => handleSave({ph_value: parseFloat(e.target.value)})}
              onKeyDown={e => e.key === 'Enter' && handleSave({ph_value: parseFloat(e.target.value)})}
              className="w-20 text-right px-2 py-1 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-color-primary)] text-xs text-[var(--aif-text-primary)] outline-none" />
          ) : (
            <button onClick={() => setEditing({ph_value: true})} className="group flex items-center gap-1">
              <span className={field.ph_value ? 'text-[var(--aif-text-primary)]' : 'text-[var(--aif-text-muted)]'}>{field.ph_value ?? t('ui.enterValue')}</span>
              <span className="opacity-0 group-hover:opacity-100 text-[10px] text-[var(--aif-color-primary)]">✎</span>
            </button>
          )}
        </div>
      </div>

      {/* Nutrient status */}
      <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 space-y-2 text-xs">
        <div className="text-[10px] text-[var(--aif-text-muted)] uppercase tracking-wide font-semibold">{t('field.nutrientSupply')}</div>
        {[['N', 'nutrient_status_n'], ['P', 'nutrient_status_p'], ['K', 'nutrient_status_k']].map(([elem, key]) => {
          const val = field[key];
          return (
            <div key={key} className="flex justify-between items-center">
              <span className="text-[var(--aif-text-muted)]">{elem} {t('field.nutrientStatus')}</span>
              <div className="flex gap-1">
                {NUTRIENT_LEVELS.map(lvl => (
                  <button key={lvl} onClick={() => handleSave({[key]: lvl})}
                    className={`w-6 h-6 rounded text-[10px] font-bold border transition-all ${val === lvl ? `${NUTRIENT_COLORS[lvl]} bg-current/20 border-current` : 'text-[var(--aif-text-muted)] border-[var(--aif-border-default)] hover:border-[var(--aif-text-muted)]'}`}
                    title={NUTRIENT_LABELS[lvl]}>
                    {lvl}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Toggles */}
      <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 space-y-2 text-xs">
        {[
          ['drainage', t('field.soilDrainage'), field?.drainage],
          ['irrigation', t('field.soilIrrigation'), field?.irrigation],
          ['nitrate_zone', t('field.soilNitrateZone'), field?.nitrate_zone],
          ['water_protection_zone', t('field.soilWaterProtection'), field?.water_protection_zone],
          ['organic_field', t('field.soilOrganic'), field?.organic_field],
        ].map(([key, label, val]) => (
          <div key={key} className="flex justify-between items-center">
            <span className="text-[var(--aif-text-muted)]">{label}</span>
            <button onClick={() => handleSave({[key]: !val})}
              className={`w-10 h-5 rounded-full transition-all ${val ? 'bg-[var(--aif-color-primary)]' : 'bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)]'}`}>
              <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform mx-0.5 ${val ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
        ))}
      </div>

      {/* Humus balance (Sprint 52) */}
      {fieldDetail52?.humus_balance?.avg_annual != null && (() => {
        const hb = fieldDetail52.humus_balance;
        const colors = { good: 'text-green-400 border-green-500/30 bg-green-500/5', ok: 'text-amber-400 border-amber-500/30 bg-amber-500/5', poor: 'text-red-400 border-red-500/30 bg-red-500/5' };
        const labels = { good: t('field.humusGood'), ok: t('field.humusOk'), poor: t('field.humusPoor') };
        const cl = hb.classification || 'ok';
        return (
          <div className={`rounded-xl border p-3 text-xs ${colors[cl]}`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-semibold">{t('field.humusBalance')}</span>
              <span className="font-bold">{labels[cl]}</span>
            </div>
            <div className="flex justify-between">
              <span className="opacity-70">{t('field.humusAvgAnnual')}</span>
              <span className="font-mono">{hb.avg_annual > 0 ? '+' : ''}{hb.avg_annual}</span>
            </div>
            <div className="flex justify-between">
              <span className="opacity-70">{t('field.analysisYears')}</span>
              <span>{hb.years}</span>
            </div>
          </div>
        );
      })()}

      {/* Soil sample placeholder */}
      <div className="rounded-xl border border-dashed border-[var(--aif-border-default)] p-3 text-center text-xs text-[var(--aif-text-muted)]">
        📋 {t('field.soilSampleHint')}
      </div>
    </div>
  );
}

// SubsidyCard extracted to views/subsidies/SubsidyDetail.jsx (A12 Sprint 4)

// MarketDetailWithWatchlist + MarketDetail extracted to views/market/ (A12 Sprint 3)


// FinanceDetail + EditableValue + FinanceCropCard + CropComparisonTable extracted to views/finance/FinanceDetail.jsx (A12 Sprint 8)
// Co-extracted private helpers: EditableValue, FinanceCropCard, CropComparisonTable
// A6-26 fix applied: 3 raw fetch → apiClient.get in refreshContributionMargin

// CashFlowDetail + CashflowSourceBadge + MonthCard + CF_SOURCE_ICONS extracted to views/cashflow/CashFlowDetail.jsx (A12 Sprint 7)
// Co-extracted private helpers: CashflowSourceBadge, MonthCard, CF_SOURCE_ICONS
// A6-26 fix applied: raw fetch → apiClient.get in refreshCashflow

// ContractsDetail extracted to views/contracts/ContractsDetail.jsx (A12 Sprint 1)

// InventoryDetail + ComingSoonDetail removed (dead code, never referenced in views dict) — A12 Sprint 9

// HerdDetail + MilkTab + FeedTab + HealthTab + EconomicsTab extracted to views/herd/HerdDetail.jsx (A12 Sprint 6)
// Co-extracted private helpers: MilkTab, FeedTab, HealthTab, EconomicsTab
// A6-26 fix applied: raw fetch → apiClient.get in HerdDetail useEffect + refresh functions

// EnergyDetail extracted to views/energy/EnergyDetail.jsx (A12 Sprint 2)


// ─── Field Activities Tab (Sprint 62 — Schlagkartei) ────────────────────────

const ACTIVITY_TYPES = [
  { key: 'FERTILIZATION',   labelKey: 'activity.typeFertilization',   icon: '🌿', color: 'text-green-400' },
  { key: 'CROP_PROTECTION', labelKey: 'activity.typeCropProtection',  icon: '💧', color: 'text-blue-400' },
  { key: 'TILLAGE',         labelKey: 'activity.typeTillage',         icon: '🚜', color: 'text-amber-700' },
  { key: 'SOWING',          labelKey: 'activity.typeSowing',          icon: '🌱', color: 'text-yellow-400' },
  { key: 'HARVEST',         labelKey: 'activity.typeHarvest',         icon: '🌾', color: 'text-orange-400' },
  { key: 'OTHER',           labelKey: 'activity.typeOther',           icon: '📋', color: 'text-gray-400' },
];

function activityTypeInfo(key) {
  return ACTIVITY_TYPES.find(t => t.key === key) || ACTIVITY_TYPES[5];
}

function NutrientBalanceBar({ balance, t }) {
  if (!balance) return null;
  const nb = balance.n_balance;
  if (!nb) return null;
  const pct = nb.limit_kg_ha > 0 ? Math.min(100, (nb.applied_kg_ha / nb.limit_kg_ha) * 100) : 0;
  const statusColor = nb.status === 'exceeded' ? 'bg-red-500' : nb.status === 'warning' ? 'bg-amber-400' : 'bg-green-500';
  const textColor = nb.status === 'exceeded' ? 'text-red-400' : nb.status === 'warning' ? 'text-amber-400' : 'text-green-400';

  return (
    <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 space-y-1.5" data-testid="nutrient-balance-bar">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-[var(--aif-text-primary)]">{t('activity.nutrientBalance')}</span>
        <span className={`font-semibold ${textColor}`}>
          {nb.status === 'exceeded' ? t('activity.duevNonCompliant') : t('activity.duevCompliant')}
        </span>
      </div>
      <div className="w-full h-2.5 bg-[var(--aif-bg-page)] rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${statusColor}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex justify-between text-[11px] text-[var(--aif-text-muted)]">
        <span>{nb.applied_kg_ha} {t('activity.nKgHa')} {t('activity.nutrientApplied')}</span>
        <span>{nb.remaining_kg_ha} {t('activity.nKgHa')} {t('activity.nutrientRemaining')}</span>
        <span>{t('activity.nutrientLimit')}: {nb.limit_kg_ha}</span>
      </div>
      {balance.nitrate_zone && (
        <div className="text-[11px] text-amber-400">⚠️ {t('activity.nitrateZone')}</div>
      )}
    </div>
  );
}

function ActivityCard({ activity, t, onDelete }) {
  const typeInfo = activityTypeInfo(activity.activity_type);
  const products = activity.products || [];
  const hasN = products.some(p => p.nutrient_n_kg_ha != null);

  return (
    <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3 space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-base">{typeInfo.icon}</span>
          <div>
            <div className="text-sm font-medium text-[var(--aif-text-primary)]">
              {t(typeInfo.labelKey)}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[11px] text-[var(--aif-text-muted)]">{activity.activity_date}</span>
              <UnifiedStatusBadge status={activity.status || 'APPROVED'} />
            </div>
          </div>
        </div>
        {onDelete && (
          <button
            onClick={() => onDelete(activity.id)}
            className="text-[var(--aif-text-muted)] hover:text-red-400 text-xs px-1.5 py-0.5 rounded"
            aria-label="delete activity"
          >
            ✕
          </button>
        )}
      </div>
      {products.length > 0 && (
        <div className="space-y-0.5 pl-7">
          {products.map((p, i) => (
            <div key={i} className="text-xs text-[var(--aif-text-secondary)]">
              {p.name} — {p.rate_per_ha} {p.rate_unit || 'kg'}/ha
              {p.nutrient_n_kg_ha != null && (
                <span className="ml-1 text-green-400">→ {p.nutrient_n_kg_ha} {t('activity.nKgHa')}</span>
              )}
            </div>
          ))}
        </div>
      )}
      {activity.notes && (
        <div className="text-xs text-[var(--aif-text-muted)] pl-7 italic">{activity.notes}</div>
      )}
    </div>
  );
}

function ProductRow({ product, onChange, onRemove, products, t }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);

  const handleNameChange = (val) => {
    onChange({ ...product, name: val });
    if (val.length >= 2 && products.length > 0) {
      const lower = val.toLowerCase();
      setSuggestions(products.filter(p => p.name.toLowerCase().includes(lower)).slice(0, 6));
      setShowSug(true);
    } else {
      setShowSug(false);
    }
  };

  const selectSuggestion = (p) => {
    onChange({ ...product, name: p.name, rate_unit: p.unit || 'kg' });
    setSuggestions([]);
    setShowSug(false);
  };

  const autoN = product._autoN;

  return (
    <div className="rounded-lg border border-[var(--aif-border-default)] bg-[var(--aif-bg-page)] p-2.5 space-y-2 relative">
      <div className="relative">
        <input
          type="text"
          value={product.name}
          onChange={e => handleNameChange(e.target.value)}
          placeholder={t('activity.productName')}
          className="w-full text-xs bg-transparent border-b border-[var(--aif-border-default)] text-[var(--aif-text-primary)] outline-none pb-1"
          data-testid="product-name-input"
        />
        {showSug && suggestions.length > 0 && (
          <div
            className="absolute left-0 right-0 z-20 bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-lg shadow-lg"
            style={{ top: '100%' }}
            data-testid="product-suggestions"
          >
            {suggestions.map((s, i) => (
              <button
                key={i}
                className="w-full text-left text-xs px-3 py-1.5 hover:bg-[var(--aif-color-primary)]/10 text-[var(--aif-text-primary)]"
                onMouseDown={e => { e.preventDefault(); selectSuggestion(s); }}
              >
                {s.name}
                {s.n_pct > 0 && <span className="ml-1 text-green-400 text-[10px]">{s.n_pct}% N</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <input
          type="number"
          value={product.rate_per_ha}
          onChange={e => onChange({ ...product, rate_per_ha: e.target.value })}
          placeholder={t('activity.productRate')}
          className="flex-1 text-xs bg-transparent border-b border-[var(--aif-border-default)] text-[var(--aif-text-primary)] outline-none pb-1"
          data-testid="product-rate-input"
        />
        <select
          value={product.rate_unit}
          onChange={e => onChange({ ...product, rate_unit: e.target.value })}
          className="text-xs bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded px-1 text-[var(--aif-text-primary)]"
        >
          {['kg', 'l', 'm³', 't', 'g'].map(u => <option key={u}>{u}</option>)}
        </select>
      </div>
      {autoN != null && (
        <div className="text-[11px] text-green-400" data-testid="auto-nutrient-display">
          → {autoN} {t('activity.nKgHa')} ({t('activity.autoCalculated')})
        </div>
      )}
      <button onClick={onRemove} className="text-[10px] text-red-400 hover:underline">✕ {t('form.remove')}</button>
    </div>
  );
}

function ActivityForm({ fieldId, token, t, onSaved, onCancel, allProducts }) {
  const today = new Date().toISOString().split('T')[0];
  const [type, setType] = useState('FERTILIZATION');
  const [date, setDate] = useState(today);
  const [products, setProducts] = useState([{ name: '', rate_per_ha: '', rate_unit: 'kg', _autoN: null }]);
  const [area, setArea] = useState('');
  const [notes, setNotes] = useState('');
  const [weather, setWeather] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Auto-calculate N when product name or rate changes
  const updateProduct = (idx, updated) => {
    const newProducts = [...products];
    const p = { ...updated };
    const ref = allProducts.find(ap => ap.name.toLowerCase() === (p.name || '').toLowerCase());
    if (ref && p.rate_per_ha && ref.n_pct != null) {
      p._autoN = Math.round(parseFloat(p.rate_per_ha) * ref.n_pct / 100 * 10) / 10;
    } else {
      p._autoN = null;
    }
    newProducts[idx] = p;
    setProducts(newProducts);
  };

  const addProduct = () => setProducts([...products, { name: '', rate_per_ha: '', rate_unit: 'kg', _autoN: null }]);
  const removeProduct = (idx) => setProducts(products.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      type,
      date,
      products: products
        .filter(p => p.name)
        .map(p => ({
          name: p.name,
          rate_per_ha: parseFloat(p.rate_per_ha) || 0,
          rate_unit: p.rate_unit,
        })),
      area_ha: area ? parseFloat(area) : undefined,
      notes: notes || undefined,
      weather: weather || undefined,
    };
    try {
      await apiClient.post(`/api/v1/aif/farm/fields/${fieldId}/activities`, payload, {
        entityType: 'activity', queueOnOffline: true,
      });
      onSaved && onSaved();
    } catch (err) {
      if (err instanceof ApiError) setError(t('activity.saveError'));
      else throw err;
    } finally {
      setSaving(false);
    }
  };

  const showProducts = type === 'FERTILIZATION' || type === 'CROP_PROTECTION' || type === 'SOWING';

  return (
    <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4 space-y-3" data-testid="activity-form">
      {/* Type selector */}
      <div>
        <div className="text-xs text-[var(--aif-text-muted)] mb-1.5">{t('activity.typeLabel')}</div>
        <div className="flex flex-wrap gap-1.5">
          {ACTIVITY_TYPES.map(at => (
            <button
              key={at.key}
              onClick={() => setType(at.key)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${
                type === at.key
                  ? 'bg-[var(--aif-color-primary)]/20 border-[var(--aif-color-primary)]/60 text-[var(--aif-color-primary)]'
                  : 'bg-[var(--aif-bg-page)] border-[var(--aif-border-default)] text-[var(--aif-text-muted)]'
              }`}
              data-testid={`type-btn-${at.key}`}
            >
              {at.icon} {t(at.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Date */}
      <div className="flex gap-3">
        <div className="flex-1">
          <div className="text-xs text-[var(--aif-text-muted)] mb-1">{t('activity.dateLabel')}</div>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="w-full text-sm bg-[var(--aif-bg-page)] border border-[var(--aif-border-default)] rounded-lg px-2.5 py-1.5 text-[var(--aif-text-primary)] outline-none"
            data-testid="date-input"
          />
        </div>
        <div className="flex-1">
          <div className="text-xs text-[var(--aif-text-muted)] mb-1">{t('activity.areaLabel')}</div>
          <input
            type="number"
            value={area}
            onChange={e => setArea(e.target.value)}
            placeholder="ha"
            className="w-full text-sm bg-[var(--aif-bg-page)] border border-[var(--aif-border-default)] rounded-lg px-2.5 py-1.5 text-[var(--aif-text-primary)] outline-none"
          />
        </div>
      </div>

      {/* Products */}
      {showProducts && (
        <div>
          <div className="text-xs text-[var(--aif-text-muted)] mb-1.5">{t('activity.productsLabel')}</div>
          <div className="space-y-2">
            {products.map((p, i) => (
              <ProductRow
                key={i}
                product={p}
                onChange={updated => updateProduct(i, updated)}
                onRemove={() => removeProduct(i)}
                products={allProducts}
                t={t}
              />
            ))}
            <button
              onClick={addProduct}
              className="text-xs text-[var(--aif-color-primary)] hover:underline"
            >
              + {t('activity.addProduct')}
            </button>
          </div>
        </div>
      )}

      {/* Notes + Weather */}
      <div className="flex gap-3">
        <input
          type="text"
          value={weather}
          onChange={e => setWeather(e.target.value)}
          placeholder={t('activity.weatherLabel')}
          className="flex-1 text-xs bg-[var(--aif-bg-page)] border border-[var(--aif-border-default)] rounded-lg px-2.5 py-1.5 text-[var(--aif-text-primary)] outline-none"
        />
      </div>
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder={t('activity.notesLabel')}
        rows={2}
        className="w-full text-xs bg-[var(--aif-bg-page)] border border-[var(--aif-border-default)] rounded-lg px-2.5 py-1.5 text-[var(--aif-text-primary)] outline-none resize-none"
      />

      {error && <div className="text-xs text-red-400">{error}</div>}

      <div className="flex gap-2 pt-1">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex-1 py-2 rounded-xl text-sm font-semibold bg-[var(--aif-color-primary)] text-white disabled:opacity-50"
        >
          {saving ? t('activity.saving') : t('activity.addActivity')}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-xl text-sm border border-[var(--aif-border-default)] text-[var(--aif-text-muted)]"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function FieldActivitiesTab({ fieldId, token, t }) {
  const [activities, setActivities] = useState(null);
  const [balance, setBalance] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const { confirm, ConfirmDialogElement } = useConfirmDialog();
  const currentYear = new Date().getFullYear();

  const load = () => {
    if (!token || !fieldId) return;
    setLoading(true);
    Promise.all([
      apiClient.get(`/api/v1/aif/farm/fields/${fieldId}/activities`)
        .then(r => r.data ?? { activities: [] }).catch(() => ({ activities: [] })),
      apiClient.get(`/api/v1/aif/farm/fields/${fieldId}/nutrient-balance?year=${currentYear}`)
        .then(r => r.data).catch(() => null),
      apiClient.get('/api/v1/aif/farm/products?category=fertilizer')
        .then(r => r.data ?? { products: [] }).catch(() => ({ products: [] })),
    ]).then(([acts, bal, prods]) => {
      setActivities(acts.activities || []);
      setBalance(bal);
      setAllProducts(prods.products || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [fieldId, token]);

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: t('activity.deleteConfirmTitle', { defaultValue: 'Maßnahme löschen?' }),
      description: t('activity.deleteConfirmBody', {
        defaultValue: 'Diese Aktion kann nicht rückgängig gemacht werden.',
      }),
      destructive: true,
    });
    if (!ok) return;
    try {
      // TODO(5b): activate concurrency — activity is 3WM
      await apiClient.delete(`/api/v1/aif/farm/activities/${id}`, {
        entityType: 'activity', queueOnOffline: true,
      });
      load();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      load();
    }
  };

  const handleSaved = () => {
    setShowForm(false);
    load();
  };

  if (loading) {
    return <div className="py-8 text-center text-xs text-[var(--aif-text-muted)]">{t('app.loading')}</div>;
  }

  return (
    <div className="space-y-3" data-testid="field-activities-tab">
      {/* Nutrient balance bar */}
      {balance && <NutrientBalanceBar balance={balance} t={t} />}

      {/* Add button */}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="w-full py-2.5 rounded-xl border-2 border-dashed border-[var(--aif-color-primary)]/40 text-sm text-[var(--aif-color-primary)] font-medium hover:border-[var(--aif-color-primary)] transition-colors"
          data-testid="add-activity-btn"
        >
          + {t('activity.addActivity')}
        </button>
      )}

      {/* Activity form */}
      {showForm && (
        <ActivityForm
          fieldId={fieldId}
          token={token}
          t={t}
          allProducts={allProducts}
          onSaved={handleSaved}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Activity timeline */}
      {activities && activities.length === 0 && !showForm && (
        <div className="text-center py-8 text-[var(--aif-text-muted)]">
          <div className="text-3xl mb-3">🚜</div>
          <div className="text-sm font-medium">{t('activity.noActivities')}</div>
          <div className="text-xs mt-1">{t('activity.noActivitiesHint')}</div>
        </div>
      )}

      {activities && activities.length > 0 && (
        <div className="space-y-2" data-testid="activity-timeline">
          {activities.map(a => (
            <ActivityCard key={a.id} activity={a} t={t} onDelete={handleDelete} />
          ))}
        </div>
      )}
      {ConfirmDialogElement}
    </div>
  );
}
